import {
	ifAuthenticated,
	ifNotAuthenticated,
	ifAuthenticatedOnline,
	AppTabs,
	Edit,
	Show,
	Create,
	Crud,
	trans
} from '@/router/index'

import Vue from 'vue'
import store from '@/store'
import MailList from '@/components/modules/mailmarketing/MailList'
import EmailCampaign from '@/components/modules/mailmarketing/EmailCampaign'
import PecCampaign from '@/components/modules/mailmarketing/PecCampaign'
import GrapesEditor from '@/components/modules/mailmarketing/GrapesEditor'
import AIBuilder from '@/components/modules/mailmarketing/AIBuilder'
import CampaignsSettings from '@/components/modules/mailmarketing/settings/CampaignsSettings'
import EmailCampaignAnalytics from '@/components/modules/mailmarketing/EmailCampaignAnalytics'
import PecCampaignAnalytics from '@/components/modules/mailmarketing/PecCampaignAnalytics'
import MailinglistsSettings from '@/components/modules/mailmarketing/settings/MailinglistsSettings'

const mailmarketingTabsMap = [
	{ path: '/mailmarketing/', model: 'maillist' },
	{ path: '/mailmarketing/templates-mailmarketing', model: 'templatemarketing' },
	{ path: '/mailmarketing/campaigns', model: 'emailcampaign' },
	{ path: '/mailmarketing/pec-campaigns', model: 'peccampaign' },
]

const normalizeMmPath = p => (p || '').replace(/\/+$/, '') || '/'

// Campagne PEC: oltre al permesso sul modello, la tab richiede il flag tenant
// pec_massive_enabled. Centralizzato qui cosi' sia mailmarketingTabGuard che
// il calcolo del primo tab disponibile trattano permesso e flag allo stesso modo.
function isTabAllowed(model) {
	if (model === 'peccampaign' && !store.getters.isPecMassiveEnabled) {
		return false
	}
	return store.getters.checkModelPermissions(model)
}

function mailmarketingTabGuard(to, from, next) {
	if (!store.getters.isOnline || !store.getters.isAuthenticated) {
		return next('/login')
	}
	const toNorm = normalizeMmPath(to.path)
	const target = mailmarketingTabsMap.find(t => normalizeMmPath(t.path) === toNorm)
	const firstAllowed = mailmarketingTabsMap.find(t => isTabAllowed(t.model))

	// /mailmarketing senza tab specifico (no slash) → vai al primo permesso
	if (to.path === '/mailmarketing') {
		if (firstAllowed) {
			return next(firstAllowed.path)
		}
		return next('/')
	}

	if (target && !isTabAllowed(target.model)) {
		if (firstAllowed && normalizeMmPath(firstAllowed.path) !== toNorm) {
			return next(firstAllowed.path)
		}
		return next('/')
	}
	next()
}

// Guard per le rotte create/edit/show delle campagne PEC: stessa logica di
// ifAuthenticatedOnline (vedi router/index.js), con in piu' il controllo del
// flag tenant. A differenza di mailmarketingTabGuard qui non c'e' un "primo tab
// disponibile" su cui ripiegare: si torna semplicemente alla lista mailmarketing.
function pecMassiveGuard(to, from, next) {
	if (!store.getters.isOnline || !store.getters.isAuthenticated) {
		return next('/login')
	}
	if (!store.getters.isPecMassiveEnabled) {
		return next('/mailmarketing/')
	}
	next()
}


export const mailmarketing = [
	{
		path: '/mailmarketing',
		beforeEnter: mailmarketingTabGuard,
		component: AppTabs,
		// Funzione anziche' oggetto statico: la tab Campagne PEC va filtrata a
		// runtime sul getter, non alla valutazione del modulo (lo store non e'
		// ancora popolato in quel momento, vedi isTabAllowed sopra).
		props: () => ({
			tabs: [
				{
					to: '/mailmarketing/',
					text: 'Mailing List',
					model: 'maillist',
				},
				{
					to: '/mailmarketing/templates-mailmarketing',
					text: 'Template',
					model: 'templatemarketing',
				},
				{
					to: '/mailmarketing/campaigns',
					text: 'Campagne',
					model: 'emailcampaign',
				},
				...(store.getters.isPecMassiveEnabled ? [{
					to: '/mailmarketing/pec-campaigns',
					text: 'Campagne PEC',
					model: 'peccampaign',
				}] : []),
			],
		}),
		children: [
			{
				path: '/mailmarketing/',
				beforeEnter: mailmarketingTabGuard,
				component: Crud,
				meta: {
					title: trans('maillist')
				},
				props: {
					modelDef: 'maillists',
					actionIcon: 'mail',
					title: 'Mailing List'
				}
			},
			{
				path: '/mailmarketing/templates-mailmarketing',
				beforeEnter: mailmarketingTabGuard,
				component: Crud,
				meta: {
					title: trans('template')
				},
				props: {
					modelDef: 'templates-mailmarketing',
					actionIcon: 'dynamic_feed',
					title: 'Template'
				}
			},
			{
				path: '/mailmarketing/campaigns',
				name: 'emailcampaigns-index',
				beforeEnter: mailmarketingTabGuard,
				component: Crud,
				meta: {
					title: trans('campaigns')
				},
				props: {
					modelDef: 'emailcampaigns',
					actionIcon: 'mail',
					title: 'Campagne'
				}
			},
			{
				path: '/mailmarketing/pec-campaigns',
				name: 'peccampaigns-index',
				beforeEnter: mailmarketingTabGuard,
				component: Crud,
				meta: {
					title: 'Campagne PEC'
				},
				props: {
					modelDef: 'peccampaigns',
					actionIcon: 'mail',
					title: 'Campagne PEC'
				}
			},
		],
	},
	{ 
		path: '/mailmarketing/maillists/create',
		name: 'maillists-create',
		beforeEnter: ifAuthenticatedOnline,
		component: Create,
		props: (route) => ({
			modelDef: 'maillists',
			redirect: route.params.redirect
		}),
	},
	{ 
		path: '/mailmarketing/maillists/:id/edit',
		name: 'maillists-edit',
		beforeEnter: ifAuthenticatedOnline,
		component: Edit,
		props: (route) => ({
			modelDef:  'maillists',
			id:  parseInt(route.params.id)
		}),
	},
	{ 
		path: '/mailmarketing/maillists/:id',
		beforeEnter: ifAuthenticatedOnline,
		component: AppTabs, 
		props: {
			tabs: [
				{
					to: '',
					text: 'Mailing List',
				},
			],
		},
		children: [
			{
				path: '/mailmarketing/maillists/:id',
				name: 'maillists-show', 
				beforeEnter: ifAuthenticatedOnline,
				component: MailList, 
				meta: {
					title: 'Mailing List'
				},
				props: (route) => ({
					id: parseInt(route.params.id)
				}), 
			},
		]
	},
	{
		path: '/mailmarketing/campaigns/create',
		name: 'emailcampaigns-create',
		beforeEnter: ifAuthenticatedOnline,
		component: Create,
		props: (route) => ({
			modelDef: 'emailcampaigns',
			modelClass: 'emailcampaign',
			redirect: route.params.redirect
		}),
	},
	{
		path: '/mailmarketing/campaigns/:id/edit',
		name: 'emailcampaigns-edit',
		beforeEnter: ifAuthenticatedOnline,
		component: Edit,
		props: (route) => ({
			modelDef:  'emailcampaigns',
			modelClass: 'emailcampaign',
			id:  parseInt(route.params.id),
		}),
	},
	{ 
		path: '/mailmarketing/campaigns/:id',
		beforeEnter: ifAuthenticatedOnline,
		component: AppTabs, 
		props: (route) => ({
			tabs: [
				{
					to: '/mailmarketing/campaigns/' + parseInt(route.params.id) + '/',
					text: 'Campagna',
				},
				{
					to: 'analytics',
					text: 'Statistiche',
				},
			],
		}),
		children: [
			{
				path: '',
				name: 'emailcampaigns-show',
				beforeEnter: ifAuthenticatedOnline,
				component: EmailCampaign,
				meta: {
					title: 'Campagna'
				},
				props: (route) => ({
					id: parseInt(route.params.id)
				}),
			},
			{
				path: '/mailmarketing/campaigns/:id/analytics',
				beforeEnter: ifAuthenticatedOnline,
				component: EmailCampaignAnalytics, 
				meta: {
					title: 'Statistiche'
				},
				props: (route) => ({
					id: parseInt(route.params.id)
				}), 
			},
		]		

	},
	{
		path: '/mailmarketing/pec-campaigns/create',
		name: 'peccampaigns-create',
		beforeEnter: pecMassiveGuard,
		component: Create,
		props: (route) => ({
			modelDef: 'peccampaigns',
			modelClass: 'peccampaign',
			redirect: route.params.redirect
		}),
	},
	{
		path: '/mailmarketing/pec-campaigns/:id/edit',
		name: 'peccampaigns-edit',
		beforeEnter: pecMassiveGuard,
		component: Edit,
		props: (route) => ({
			modelDef: 'peccampaigns',
			modelClass: 'peccampaign',
			id: parseInt(route.params.id),
		}),
	},
	{
		path: '/mailmarketing/pec-campaigns/:id',
		beforeEnter: pecMassiveGuard,
		component: AppTabs,
		props: (route) => ({
			tabs: [
				{
					to: '/mailmarketing/pec-campaigns/' + parseInt(route.params.id) + '/',
					text: 'Dettagli',
				},
				{
					to: '/mailmarketing/pec-campaigns/' + parseInt(route.params.id) + '/analytics',
					text: 'Statistiche',
				},
			],
		}),
		children: [
			{
				path: '',
				name: 'peccampaigns-show',
				beforeEnter: pecMassiveGuard,
				component: PecCampaign,
				meta: {
					title: 'Campagna PEC'
				},
				props: (route) => ({
					id: parseInt(route.params.id)
				}),
			},
			{
				path: '/mailmarketing/pec-campaigns/:id/analytics',
				name: 'peccampaigns-analytics',
				beforeEnter: ifAuthenticatedOnline,
				component: PecCampaignAnalytics,
				meta: {
					title: 'Statistiche'
				},
				props: (route) => ({
					id: parseInt(route.params.id)
				}),
			},
		],
	},
	{ 
		path: '/mailmarketing/templates-mailmarketing/create',
		name: 'templates-mailmarketing-create',
		beforeEnter: ifAuthenticatedOnline,
		component: Create,
		props: (route) => ({
			modelDef: 'templates-mailmarketing',
			redirect: route.params.redirect,
			defaultInput: {type: 'mailmarketing'},
		}),
	},
	{ 
		path: '/mailmarketing/templates-mailmarketing/:id/edit',
		name: 'templates-mailmarketing-edit',
		beforeEnter: ifAuthenticatedOnline,
		component: Edit,
		props: (route) => ({
			modelDef:  'templates-mailmarketing',
			id:  parseInt(route.params.id),
			defaultInput: {type: 'mailmarketing'},
		}),
	},
	{
		path: '/mailmarketing/templates-mailmarketing/:id/builder',
		name: 'templates-mailmarketing-builder',
		beforeEnter: ifAuthenticatedOnline,
		component: GrapesEditor,
		props: (route) => ({
			modelDef:  'templates-mailmarketing',
			id:  parseInt(route.params.id),
			defaultInput: {type: 'mailmarketing'},
		}),
	},
	{
		path: '/mailmarketing/templates-mailmarketing/:id/ai-builder',
		name: 'templates-mailmarketing-ai-builder',
		beforeEnter: ifAuthenticatedOnline,
		component: AIBuilder,
		// menu laterale a icone: la mail ha bisogno della larghezza
		meta: {compactDrawer: true},
		props: (route) => ({
			modelDef: 'templates-mailmarketing',
			id: parseInt(route.params.id),
			defaultInput: {type: 'mailmarketing'},
		}),
	},
	{
		path: '/mailmarketing/templates-mailmarketing/:id',
		beforeEnter: ifAuthenticatedOnline,
		component: AppTabs, 
		props: {
			tabs: [
				{
					to: '',
					text: 'Template',
				},
			],
		},
		children: [
			{
				path: '/mailmarketing/templates-mailmarketing/:id',
				name: 'templates-mailmarketing-show',
				beforeEnter: ifAuthenticatedOnline,
				component: Show, 
				meta: {
					title: 'Template'
				},
				props: (route) => ({
					id: parseInt(route.params.id),
					modelDef:  'templates-mailmarketing',
					modelClass:  'template',
					activities:  true,
				}), 
			},
		]		
	},
	{ 
		path: '/settingsmailmarketing/',
		name: 'settingsmailmarketing',
		beforeEnter: ifAuthenticatedOnline,
		component: AppTabs,
		props: {
			tabs: [
				{
					to: '/settingsmailmarketing',
					text: 'Mailing List',
				},
				{
					to: '/settingsmailmarketing/campaigns',
					text: 'Campagne',
				},
			],
		},
		children: [
			{
				path: '/settingsmailmarketing/',
				beforeEnter: ifAuthenticatedOnline,
				component: MailinglistsSettings,
			},
			{
				path: '/settingsmailmarketing/campaigns',
				beforeEnter: ifAuthenticatedOnline,
				component: CampaignsSettings,
			},			
		],
	},
]